import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Logo } from '@insight-health-ai/roseate/components';
import { useTranslation } from 'react-i18next';
import {
  getOfferDetails,
  respondToOffer,
  type OfferDetails,
} from 'src/api/waitlist';

type ViewState =
  | 'loading'
  | 'offer'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'error';

const WaitlistOffer = () => {
  const { token, backfillId } = useParams<{
    token: string;
    backfillId: string;
  }>();
  const { t, i18n } = useTranslation('misc');

  const [view, setView] = useState<ViewState>('loading');
  const [offer, setOffer] = useState<OfferDetails | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token || !backfillId) {
      setView('error');
      return;
    }

    getOfferDetails(token, backfillId)
      .then(({ data }) => {
        setOffer(data);
        if (data.offer_status === 'expired') setView('expired');
        else if (data.offer_status === 'accepted') setView('accepted');
        else setView('offer');
      })
      .catch(() => setView('error'));
  }, [token, backfillId]);

  const handleRespond = useCallback(
    async (accepted: boolean) => {
      if (!token || !offer || submitting) return;
      setSubmitting(true);

      try {
        await respondToOffer(token, {
          backfill_id: offer.backfill_id,
          accepted,
        });
        setView(accepted ? 'accepted' : 'declined');
      } catch {
        setView('error');
      } finally {
        setSubmitting(false);
      }
    },
    [token, offer, submitting]
  );

  const formatDateTime = (iso: string | null) => {
    if (!iso) return { date: '', time: '' };
    const locale = i18n.language || 'en-US';
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString(locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
      time: d.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
  };

  const { date: slotDate, time: slotTime } = formatDateTime(
    offer?.slot_time ?? null
  );

  return (
    <main className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        {view === 'loading' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <p className="text-gray-500">{t('waitlist.loading')}</p>
          </div>
        )}

        {view === 'offer' && offer && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-blue-600 px-6 py-5">
              <h1 className="text-white text-lg font-semibold text-center">
                {t('waitlist.title')}
              </h1>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t('waitlist.description', { practice: offer.practice_name })}
              </p>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">{t('waitlist.provider')}:</span>{' '}
                  {offer.provider_name}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">{t('waitlist.date')}:</span>{' '}
                  {slotDate}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('waitlist.time')}:</span>{' '}
                  {slotTime}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleRespond(false)}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {t('waitlist.decline')}
                </button>
                <button
                  onClick={() => handleRespond(true)}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? t('waitlist.submitting') : t('waitlist.accept')}
                </button>
              </div>
            </div>
          </div>
        )}

        {view === 'accepted' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t('waitlist.acceptedTitle')}
            </h2>
            <p className="text-gray-600">{t('waitlist.acceptedMessage')}</p>
          </div>
        )}

        {view === 'declined' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t('waitlist.declinedTitle')}
            </h2>
            <p className="text-gray-600">{t('waitlist.declinedMessage')}</p>
          </div>
        )}

        {view === 'expired' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t('waitlist.expiredTitle')}
            </h2>
            <p className="text-gray-600">{t('waitlist.expiredMessage')}</p>
          </div>
        )}

        {view === 'error' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {t('waitlist.errorTitle')}
            </h2>
            <p className="text-gray-600">{t('waitlist.errorMessage')}</p>
          </div>
        )}
      </div>
    </main>
  );
};

export { WaitlistOffer };
